// ink-component
import React, { HTMLAttributes } from 'react';
import styled from 'styled-components';

import getGlobalProps from '../../helpers/constants/global-props';

import Grid from '../Grid/Grid';
import Row from '../Grid/Row/Row';
import Column from '../Grid/Column/Column';

import PageBody from './components/PageBody/PageBody';
import PageContent from './components/PageContent/PageContent';
import PageHeader from './components/PageHeader/PageHeader';
import PageSidebar from './components/PageSidebar/PageSidebar';
import PageTitle from './components/PageTitle/PageTitle';

export interface PageProps extends HTMLAttributes<HTMLDivElement> {
  /** Determines if the page will take all the available screen width or
   * will be a large centered container with narrow gaps on the sides. */
  width?: 'full' | 'large';
}

const PageElement = styled.div<PageProps>`
  max-width: 2000px;
  margin: auto;
`;

const globalProps = getGlobalProps({});

/**
 * @purpose Initiates every page UI structure
 * @group Page
 * @categories layout
 * @HTMLType HTMLDivElement
 */
function Page(props: PageProps) {
  const { className, style, ...otherProps } = props;

  const renderChildren = () => {
    if (props.width === 'large') {
      return (
        <Column
          mobile="12-of-12"
          tabletPortrait="12-of-12"
          tabletLandscape="10-of-12"
          desktop="10-of-12"
          desktopLarge="8-of-12"
          offsetTabletLandscape="1-of-12"
          offsetDesktop="1-of-12"
          offsetDesktopLarge="2-of-12"
        >
          {props.children}
        </Column>
      );
    }

    return <Column mobile="12-of-12">{props.children}</Column>;
  };

  return (
    <PageElement {...otherProps} {...globalProps}>
      <Grid>
        <Row>{renderChildren()}</Row>
      </Grid>
    </PageElement>
  );
}

Page.defaultProps = {
  width: 'full',
};

Page.Header = PageHeader;
Page.Title = PageTitle;
Page.Body = PageBody;
Page.Sidebar = PageSidebar;
Page.Content = PageContent;

export { PageBodyProps } from './components/PageBody/PageBody';
export { PageContentProps } from './components/PageContent/PageContent';
export { PageHeaderProps } from './components/PageHeader/PageHeader';
export { PageSidebarProps } from './components/PageSidebar/PageSidebar';
export { PageTitleProps } from './components/PageTitle/PageTitle';

Page.displayName = 'Page';

export default Page;
